// Initial variant selection of a product: first available combination, else the main-price one, else the first.

export const get_default_mix_variant = (data) => {
  const available_mix_variant = data?.mix_variant?.find(
    (variant) => variant?.price?.price > 0 && variant?.details?.count > 0,
  );
  const main_mix_variant = data?.mix_variant?.find(
    (variant) => variant?.is_main_price,
  );
  const first_mix_variant = data?.mix_variant?.[0];
  return available_mix_variant
    ? available_mix_variant
    : main_mix_variant
      ? main_mix_variant
      : first_mix_variant;
};

// `{ [variant_id]: "<variant_id>-<label_id>" }` for the labels named in `labels_query` ("key,key", the
// `?selected-labels=` link parameter) or, without one, for the default combination.
// The exception variant (`exception_variant_key`, e.g. "capacities") starts on its first label, or on the label named in
// `preselected_label_ids` (`{ [variant_id]: label_id }`, from `?sku=` / Torob: get_preselected_selection). Its labels
// are read from `variant.variant.labels`, else `variant.labels`, where the product queries carry them (core 14 read
// those: without the fallback the key was "<id>-undefined" and no combination was selected; memory-market).
export const get_initial_selected_labels = ({ data, labels_query, exception_variant_key, preselected_label_ids = {} } = {}) => {
  let obj = {};
  if (labels_query?.length > 0) {
    labels_query?.split(",")?.forEach((label_key) => {
      data?.variants?.forEach((variant) => {
        const label_id = variant?.labels?.find(
          (label) => label?.key === label_key,
        )?.id;

        if (label_id) {
          const variant_id = variant?.variant?.id;
          obj[variant_id] = `${variant_id}-${label_id}`;
        }
      });
    });
  } else {
    get_default_mix_variant(data)?.keys?.forEach((key) => (obj[key?.split("-")?.[0]] = key));
  }
  if (exception_variant_key) {
    const variant = data?.variants?.find(
      (variant) => variant?.variant?.key === exception_variant_key,
    );
    if (variant) {
      const variant_id = variant?.variant?.id;
      const labels = variant?.variant?.labels || variant?.labels;
      const label_id = preselected_label_ids?.[variant_id] || labels?.[0]?.id;
      obj = {
        ...obj,
        [variant_id]: `${variant_id}-${label_id}`,
      };
    }
  }

  return obj;
};

// The combination a link asks for, as core 14 read it: `?sku=<keys>` ("<variant_id>-<label_id>,..."), or a Torob link
// (`?page-source=TOROB`) whose last slug is those keys. undefined when there is none.
export const find_preselected_mix_variant = (data, search_params) => {
  const slugs_tree = data?.slugs_tree;
  if (slugs_tree?.length > 1 && search_params?.get?.("page-source") === "TOROB") {
    const last_slug = decodeURIComponent(slugs_tree[slugs_tree.length - 1]);
    return data?.mix_variant?.find((mx) => mx?.keys?.join(",") === last_slug);
  }
  const sku = search_params?.get?.("sku");
  return sku?.length > 0 ? data?.mix_variant?.find((mx) => mx?.keys?.join(",") === sku) : undefined;
};

// `{ initial_labels, preselected_label_ids }` for useProductBase when a link preselects a combination
// (find_preselected_mix_variant), else null. `initial_labels` is the `?selected-labels=` form of it ("key,key").
export const get_preselected_selection = (data, search_params) => {
  const mix_variant = find_preselected_mix_variant(data, search_params);
  if (!mix_variant) return null;
  const preselected_label_ids = {};
  const label_keys = [];
  mix_variant?.keys?.forEach((key) => {
    const [variant_id, label_id] = String(key).split("-");
    preselected_label_ids[variant_id] = label_id;
    const label_key = data?.variants
      ?.find((variant) => variant?.variant?.id === variant_id)
      ?.labels?.find((label) => label?.id === label_id)?.key;
    if (label_key) label_keys.push(label_key);
  });
  return { initial_labels: label_keys.join(","), preselected_label_ids };
};
